<script setup lang="ts">
import type { IconsKeys } from '../icon/types'
import { SnackbarTheme, SnackbarType } from './types'

type Props = {
  message: string
  type?: SnackbarType
  theme?: SnackbarTheme
  hyphens?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: SnackbarType.MESSAGE,
  theme: SnackbarTheme.DARK,
  hyphens: true,
})

const icon: Record<SnackbarType, IconsKeys> = {
  [SnackbarType.MESSAGE]: 'alert-circle',
  [SnackbarType.SUCCESS]: 'checkmark-circle',
  [SnackbarType.ERROR]: 'alert-circle',
  [SnackbarType.WARNING]: 'alert-circle',
}

const componentClasses = computed(() => {
  return [
    'app-snackbar-item',
    `app-snackbar-item--type-${props.type}`,
    `app-snackbar-item--theme-${props.theme}`,
    { 'app-snackbar-item--hyphens': props.hyphens },
  ]
})
</script>

<template>
  <div data-test-id="snackbar-item" :class="componentClasses">
    <div class="app-snackbar-item__content">
      <the-icon
        class="app-snackbar-item__icon"
        color="currentColor"
        :name="icon[type]"
      />

      <div class="app-snackbar-item__message">{{ message }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-snackbar-item {
  border-radius: 12px;

  &__content {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 12px;
  }

  &__icon {
    align-self: flex-start;
  }

  &__message {
    font-family: 'Raleways', verdana, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
  }

  /* Type Message */
  &--type-message#{&}--theme-light {
    color: var(--ui-color-sandy-950);
    background-color: var(--ui-color-sandy-100);
  }

  &--type-message#{&}--theme-dark {
    color: var(--ui-color-white);
    background-color: var(--ui-color-sandy-950);
  }

  /* Type Success */
  &--type-success#{&}--theme-light {
    color: var(--ui-color-green);
    background-color: var(--ui-color-green-light);
  }

  &--type-success#{&}--theme-dark {
    color: var(--ui-color-white);
    background-color: var(--ui-color-green);
  }

  /* Type Error */
  &--type-error#{&}--theme-light {
    color: var(--ui-color-red);
    background-color: var(--ui-color-red-light);
  }

  &--type-error#{&}--theme-dark {
    color: var(--ui-color-white);
    background-color: var(--ui-color-red);
  }

  /* Type Error */
  &--type-warning#{&}--theme-light {
    color: var(--ui-color-yellow);
    background-color: var(--ui-color-yellow-light);
  }

  &--type-warning#{&}--theme-dark {
    color: var(--ui-color-white);
    background-color: var(--ui-color-yellow);
  }

  &--hyphens {
    .app-snackbar-item__message {
      -webkit-hyphens: auto;
      hyphens: auto;
    }
  }
}
</style>
